import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import * as libphonenumber from 'google-libphonenumber';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Trash } from 'src/components/icons/trash';
import { PhoneNumberInput } from 'src/components/phone_number_input';
import { RadioGroupField } from 'src/components/radio_group';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import {
  useEffectiveOrganization,
  useUser,
  useIsOrderingPhysician,
} from 'src/hooks/use_auth';
import { useDebounce } from 'src/hooks/use_debounce';
import { OrderingProviderFieldsFragment } from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess } from 'src/utils';

// Enum for report delivery options
export enum ReportDeliveryOption {
  FaxAndPortal = 'fax-and-portal',
  PortalOnly = 'portal-only',
}

// Report delivery options
const REPORT_DELIVERY_OPTIONS: Array<{ value: string; name: string }> = [
  {
    value: ReportDeliveryOption.FaxAndPortal,
    name: 'Receive Fax and Download from Portal',
  },
  {
    value: ReportDeliveryOption.PortalOnly,
    name: 'Download from Portal only',
  },
];

// Schema factory that creates schema with async email validation
const createOrderingProviderFormSchema = (
  orderRepository: LdtOrderRepository,
  queryClient: QueryClient,
  initialProvider?: OrderingProviderFieldsFragment | null,
  isInternational?: boolean,
  isSiteNameRequired?: boolean,
) =>
  z.object({
    firstName: z.string().min(1, 'First Name is required'),
    lastName: z.string().min(1, 'Last Name is required'),
    npi: isInternational
      ? z.string().optional()
      : z
          .string()
          .min(1, 'NPI is required')
          .regex(/^\d{10}$/, 'NPI must be exactly 10 digits'),
    email: z
      .string()
      .optional()
      .superRefine(async (val, ctx) => {
        // If no email, skip validation
        if (!val) return;

        // Basic email format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(val)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please enter a valid email address',
          });
          return;
        }

        // Async validation using ensureQueryData for caching
        try {
          const validation = await queryClient.ensureQueryData({
            queryKey: ['validateOrderingPhysicianEmail', val],
            queryFn: async () => {
              return orderRepository.validateOrderingPhysicianEmail(val);
            },
            staleTime: 30000, // Cache for 30 seconds
          });

          if (!validation.isValid) {
            ctx.addIssue({
              code: 'custom',
              message: 'Please enter a valid email address',
            });
          } else if (
            validation.isAlreadyUsed &&
            val.toLowerCase() !==
              initialProvider?.orderingPhysician?.email?.toLowerCase()
          ) {
            ctx.addIssue({
              code: 'custom',
              message:
                'This email is already in use by another ordering provider',
            });
          }
        } catch (error) {
          // If validation fails, don't block submission - let server handle it
          console.error('Email validation error:', error);
        }
      }),
    receiveReportsByFax: z.enum(
      [ReportDeliveryOption.FaxAndPortal, ReportDeliveryOption.PortalOnly],
      {
        error: 'Please select how you would like to receive your report',
      },
    ),
    siteName: isSiteNameRequired
      ? z.string().trim().min(1, 'Site Name is required')
      : z.string().optional(),
    faxNumber: z.string().optional(), // E.164 formatted phone number
    faxRegionCode: z.string().optional(), // Region code (e.g., 'US', 'CA')
  });

export type OrderingProviderFormData = z.infer<
  ReturnType<typeof createOrderingProviderFormSchema>
>;

// Codependent fax validation: runs in custom resolver so trigger('faxNumber') validates correctly
function validateFaxNumber(data: OrderingProviderFormData): string | null {
  if (data.receiveReportsByFax !== ReportDeliveryOption.FaxAndPortal) {
    return null;
  }
  const hasLength = Boolean(
    data.faxNumber &&
    data.faxNumber.trim().length > 0 &&
    data.faxNumber.trim().length < 15 &&
    data.faxRegionCode &&
    data.faxRegionCode.trim().length === 2,
  );
  if (!hasLength) {
    return 'Please enter a valid fax number that does not exceed 15 digits';
  }
  try {
    const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
    const phoneNumber = phoneUtil.parseAndKeepRawInput(
      data.faxNumber || '',
      data.faxRegionCode,
    );
    if (!phoneUtil.isValidNumber(phoneNumber)) {
      return 'Please enter a valid fax number';
    }
    return null;
  } catch {
    return 'Please enter a valid fax number';
  }
}

function createOrderingProviderCustomResolver(
  schema: ReturnType<typeof createOrderingProviderFormSchema>,
) {
  return async (
    data: OrderingProviderFormData,
    context: unknown,
    options: ResolverOptions<OrderingProviderFormData>,
  ): Promise<ResolverResult<OrderingProviderFormData>> => {
    const zodResult = await zodResolver(schema)(data, context, options);
    const customErrors = { ...zodResult.errors };
    const faxError = validateFaxNumber(data);

    if (faxError) {
      customErrors.faxNumber = {
        type: 'custom',
        message: faxError,
      };
    } else {
      delete customErrors.faxNumber;
    }

    return {
      values: data,
      errors: customErrors,
    } as ResolverResult<OrderingProviderFormData>;
  };
}

interface OrderingProviderFormProps {
  open: boolean;
  onClose: () => void;
  initialProvider?: OrderingProviderFieldsFragment | null;
  onSuccess?: (provider: OrderingProviderFieldsFragment) => void;
  onDelete?: (providerId: string) => void;
  orderRepository: LdtOrderRepository;
}

export const OrderingProviderForm: FC<OrderingProviderFormProps> = ({
  open,
  onClose,
  initialProvider,
  onSuccess,
  onDelete,
  orderRepository,
}) => {
  const fetcher = useFetcher();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useUser();
  const isOrderingPhysicianUser = useIsOrderingPhysician();
  const workflowType = useWorkflowType();
  const isInternational = useEffectiveOrganization().features.isInternational;
  const isEditMode = !!initialProvider;
  const showSiteName = workflowType === WorkflowType.ANCILLARY_LAB;
  // Determine if the site name is locked based on whether it has been set
  const isSiteNameLocked = Boolean(initialProvider?.siteName?.trim());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Track the last processed result data reference to avoid reprocessing
  const lastProcessedDataRef = useRef<unknown>(null);
  // Track if we've submitted a form (to know we're expecting a result)
  const hasSubmittedRef = useRef<boolean>(false);
  // Track if we're currently submitting to prevent double submissions
  const isSubmittingRef = useRef<boolean>(false);

  // Create schema with async email validation
  const schema = useMemo(
    () =>
      createOrderingProviderFormSchema(
        orderRepository,
        queryClient,
        initialProvider,
        isInternational,
        showSiteName,
      ),
    [
      orderRepository,
      queryClient,
      initialProvider,
      isInternational,
      showSiteName,
    ],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    trigger,
  } = useForm<OrderingProviderFormData>({
    resolver: createOrderingProviderCustomResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      firstName: initialProvider?.orderingPhysician?.firstName || '',
      lastName: initialProvider?.orderingPhysician?.lastName || '',
      npi: initialProvider?.orderingPhysician?.npi || '',
      email:
        initialProvider?.orderingPhysician?.email ||
        (isOrderingPhysicianUser && !initialProvider ? user?.email : undefined),
      // Only set a default report delivery option when editing an existing provider.
      // For new providers, no option should be pre-selected so users must choose.
      receiveReportsByFax: initialProvider
        ? initialProvider.receiveReportsByFax
          ? ReportDeliveryOption.FaxAndPortal
          : ReportDeliveryOption.PortalOnly
        : undefined,
      siteName: initialProvider?.siteName || '',
      faxNumber: initialProvider?.siteFaxNumber || '',
      faxRegionCode: initialProvider?.siteFaxRegionCode || 'US',
    },
  });

  // Watch the receiveReportsByFax value to conditionally show/hide fax number
  const receiveReportsByFax = watch('receiveReportsByFax');
  const showFaxNumber =
    receiveReportsByFax === ReportDeliveryOption.FaxAndPortal;

  // Debounced email onChange handler - only triggers validation when user stops typing
  const debouncedTriggerEmail = useDebounce((email: string) => {
    if (email) {
      trigger('email');
    }
  }, 500);

  // Autopopulate email for physician users when creating a new provider
  useEffect(() => {
    if (open && !isEditMode && isOrderingPhysicianUser && user?.email) {
      const currentEmail = watch('email');
      if (!currentEmail) {
        setValue('email', user.email, { shouldValidate: true });
      }
    }
  }, [open, isEditMode, isOrderingPhysicianUser, user?.email, setValue, watch]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Clear fax number and region code when switching to portal-only
  useEffect(() => {
    if (receiveReportsByFax === ReportDeliveryOption.PortalOnly) {
      setValue('faxNumber', '');
      setValue('faxRegionCode', '');
    } else if (receiveReportsByFax === ReportDeliveryOption.FaxAndPortal) {
      // Ensure faxRegionCode is set to default 'US' when fax input is shown
      const currentRegionCode = watch('faxRegionCode');
      if (!currentRegionCode) {
        setValue('faxRegionCode', 'US', { shouldValidate: false });
      }
    }
  }, [receiveReportsByFax, setValue, watch]);

  // Track when we submit to know we're expecting a result
  useEffect(() => {
    if (fetcher.state === 'submitting') {
      hasSubmittedRef.current = true;
      isSubmittingRef.current = true;
    } else if (fetcher.state === 'idle') {
      // Reset submitting flag when fetcher is idle (submission completed or failed)
      isSubmittingRef.current = false;
    }
  }, [fetcher.state]);

  // Handle action results - only process when dialog is open, we've submitted, and result hasn't been processed
  useEffect(() => {
    if (
      open &&
      fetcher.data &&
      fetcher.state === 'idle' &&
      hasSubmittedRef.current &&
      lastProcessedDataRef.current !== fetcher.data
    ) {
      const result = fetcher.data as {
        success: boolean;
        orderingProvider?: OrderingProviderFieldsFragment;
        action: OrderAction;
        error?: string;
      };

      // Handle create/update actions
      const expectedAction = isEditMode
        ? OrderAction.UPDATE_ORDERING_PROVIDER
        : OrderAction.CREATE_ORDERING_PROVIDER;

      if (result.action === expectedAction) {
        // Mark this result as processed
        lastProcessedDataRef.current = fetcher.data;
        hasSubmittedRef.current = false;

        if (result.success) {
          notifySuccess(
            `Ordering provider ${isEditMode ? 'updated' : 'created'} successfully.`,
          );
          // Allow closing even if required, since provider was successfully created
          onClose();
          if (result.orderingProvider && onSuccess) {
            onSuccess(result.orderingProvider);
          }
        } else {
          notifyError(
            `Failed to ${isEditMode ? 'update' : 'create'} ordering provider.`,
          );
        }
      }

      // Handle delete action
      if (result.action === OrderAction.DELETE_ORDERING_PROVIDER) {
        // Mark this result as processed
        lastProcessedDataRef.current = fetcher.data;
        hasSubmittedRef.current = false;

        if (result.success) {
          notifySuccess('Ordering provider deleted successfully.');
          setDeleteDialogOpen(false);
          // Allow closing even if required, since delete action completed successfully
          onClose();
          // Notify parent component about the deletion
          if (initialProvider?.id && onDelete) {
            onDelete(initialProvider.id);
          }
        } else {
          notifyError('Failed to delete ordering provider.');
          setDeleteDialogOpen(false);
        }
      }
    }
  }, [
    fetcher.data,
    fetcher.state,
    initialProvider?.id,
    isEditMode,
    onClose,
    onDelete,
    onSuccess,
    open,
  ]);

  const onSubmit = useCallback(
    (data: OrderingProviderFormData) => {
      // Prevent multiple submissions
      if (isSubmittingRef.current || fetcher.state === 'submitting') {
        return;
      }

      isSubmittingRef.current = true;

      const providerData = {
        ...(isEditMode && initialProvider ? { id: initialProvider.id } : {}),
        orderingPhysician: {
          firstName: data.firstName,
          lastName: data.lastName,
          ...(data.npi && !isInternational ? { npi: data.npi } : {}),
          email: data.email,
        },
        receiveReportsByFax:
          data.receiveReportsByFax === ReportDeliveryOption.FaxAndPortal,
        ...(showSiteName ? { siteName: data.siteName?.trim() || null } : {}),
        siteFaxNumber: data.faxNumber || undefined,
        siteFaxRegionCode: data.faxRegionCode || undefined,
      };

      const formData = new FormData();
      formData.append(
        'action',
        isEditMode
          ? OrderAction.UPDATE_ORDERING_PROVIDER
          : OrderAction.CREATE_ORDERING_PROVIDER,
      );
      formData.append('providerData', JSON.stringify(providerData));

      fetcher.submit(formData, {
        method: 'post',
        action: location.pathname,
      });
    },
    [
      fetcher,
      initialProvider,
      isEditMode,
      location.pathname,
      isInternational,
      showSiteName,
    ],
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!initialProvider?.id) {
      return;
    }

    // Prevent multiple submissions
    if (isSubmittingRef.current || fetcher.state === 'submitting') {
      return;
    }

    isSubmittingRef.current = true;
    hasSubmittedRef.current = true;

    const formData = new FormData();
    formData.append('action', OrderAction.DELETE_ORDERING_PROVIDER);
    formData.append('providerId', initialProvider.id);

    fetcher.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [initialProvider, fetcher, location.pathname]);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const formTitle = `${isEditMode ? 'Edit' : 'Add'} ordering provider`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <Box
        component='form'
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        noValidate
        sx={{ width: '100%' }}
      >
        <DialogTitle sx={{ p: 0, textAlign: 'left' }}>{formTitle}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              color: 'text.secondary',
              mb: 2,
            }}
          >
            *Indicates a required field
          </Typography>

          <Stack spacing={2}>
            {/* First Name and Last Name Row */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='firstName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      label='First Name'
                      data-testid='orderingProvider-firstName'
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message?.toString() ?? ' '}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='lastName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      label='Last Name'
                      data-testid='orderingProvider-lastName'
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message?.toString() ?? ' '}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>

            {/* NPI and Email Row */}
            <ResponsiveFormRow>
              {!isInternational && (
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='npi'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        label='NPI'
                        data-testid='orderingProvider-npi'
                        error={!!errors.npi}
                        helperText={errors.npi?.message?.toString() ?? ' '}
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 10,
                              pattern: '[0-9]*',
                              inputMode: 'numeric',
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='email'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type='email'
                      label='Email'
                      data-testid='orderingProvider-email'
                      error={!!errors.email}
                      helperText={errors.email?.message?.toString() ?? ' '}
                      disabled={isOrderingPhysicianUser}
                      onChange={(e) => {
                        // Only allow changes if not a physician user creating a new provider
                        if (!(isOrderingPhysicianUser && !isEditMode)) {
                          field.onChange(e);
                          debouncedTriggerEmail(e.target.value);
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>

            {showSiteName && (
              <Controller
                name='siteName'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    label='Site Name'
                    data-testid='orderingProvider-siteName'
                    error={!!errors.siteName}
                    helperText={errors.siteName?.message?.toString() ?? ' '}
                    slotProps={{
                      input: {
                        disabled: isSiteNameLocked,
                      },
                    }}
                  />
                )}
              />
            )}

            <Controller
              name='receiveReportsByFax'
              control={control}
              render={({ field }) => (
                <RadioGroupField
                  {...field}
                  data-testid='orderingProvider-reportDelivery'
                  required
                  label='How would you like to receive your report?'
                  options={REPORT_DELIVERY_OPTIONS}
                  validationText={errors.receiveReportsByFax?.message ?? ''}
                />
              )}
            />

            {/* Fax Number Input (conditional) */}
            {showFaxNumber && (
              <Controller
                name='faxNumber'
                control={control}
                render={({ field }) => {
                  return (
                    <PhoneNumberInput
                      {...field}
                      required
                      fullWidth
                      label='Fax'
                      data-testid='orderingProvider-faxNumber'
                      onChange={(state) => {
                        field.onChange(state.e164);
                        setValue('faxRegionCode', state.regionCode);

                        if (state.isValid) {
                          trigger('faxNumber');
                        }
                      }}
                      defaultRegionCode={
                        initialProvider?.siteFaxRegionCode ?? 'US'
                      }
                      onBlur={field.onBlur}
                      error={!!errors.faxNumber}
                      helperText={errors.faxNumber?.message?.toString() ?? ' '}
                    />
                  );
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: {
              xs: 'stretch',
              sm: isEditMode ? 'space-between' : 'flex-end',
            },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: isEditMode ? 0 : 2,
            '& > :not(style) ~ :not(style)': {
              marginLeft: 0,
            },
          }}
        >
          {isEditMode && (
            <Button
              onClick={handleDeleteClick}
              size='large'
              type='button'
              color='error'
              data-testid='orderingProvider-delete'
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 1, sm: 1 },
              }}
            >
              DELETE
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 0.25, sm: 2 },
              order: { xs: 2, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Button
              onClick={handleClose}
              size='large'
              type='button'
              data-testid='orderingProvider-close'
              sx={{
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              CLOSE
            </Button>
            <Button
              type='submit'
              variant='contained'
              size='large'
              data-testid='orderingProvider-submit'
              disabled={isSubmitting || fetcher.state === 'submitting'}
              sx={{
                width: { xs: '100%', sm: '160px' },
              }}
            >
              {isEditMode ? 'SAVE' : 'CREATE'}
            </Button>
          </Box>
        </DialogActions>
      </Box>
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, DELETE',
          secondaryButtonText: 'NO',
          title: 'Delete Ordering Provider?',
          subtitle:
            'Are you sure you want to delete this ordering provider? This action cannot be undone.',
          type: FeedbackType.Error,
          icon: Trash,
        }}
        isDialogOpen={deleteDialogOpen}
        onPrimaryButtonClick={handleConfirmDelete}
        onSecondaryButtonClick={handleCloseDeleteDialog}
        handleDialogClose={handleCloseDeleteDialog}
      />
    </Dialog>
  );
};
